import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, alpha, keyframes } from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Circle as StatusIcon
} from '@mui/icons-material';
import api from '../../utils/api';

// Scrolling animation
const scroll = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
`;

// Flashing animation
const flash = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Get icon for message type
const getMessageIcon = (type) => {
  switch (type) {
    case 'warning': return <WarningIcon sx={{ fontSize: 16 }} />;
    case 'success': return <SuccessIcon sx={{ fontSize: 16 }} />;
    case 'error': return <ErrorIcon sx={{ fontSize: 16 }} />;
    default: return <InfoIcon sx={{ fontSize: 16 }} />;
  }
};

// Get color for message type
const getMessageColor = (type) => {
  switch (type) {
    case 'warning': return '#f59e0b';
    case 'success': return '#10b981';
    case 'error': return '#ef4444';
    default: return '#3b82f6';
  }
};

const MarqueeBar = ({ isOnline, isIdle, userName }) => {
  const [messages, setMessages] = useState([]);
  const [systemMessages, setSystemMessages] = useState([]);

  // Fetch marquee messages
  const fetchMessages = useCallback(async () => {
    try {
      const response = await api.get('/settings/marquee/active');
      setMessages(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch marquee messages:', error);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMessages, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Generate system status messages based on punch status
  useEffect(() => {
    const statusMsgs = [];
    
    // isOnline means user has an active session and is not idle
    // isIdle means user has no active session or idle timeout exceeded
    if (isOnline && !isIdle) {
      // User IS punched in and active - show helpful tip
      statusMsgs.push({
        id: 'status-online',
        message: `✅ You are currently Punched In. When you finish work, click "Punch Out" or simply logout which will automatically punch you out.`,
        message_type: 'success',
        priority: 50
      });
    } else {
      // User is NOT punched in OR is idle - show RED error message for urgency
      statusMsgs.push({
        id: 'status-idle',
        message: `🔴 ${userName || 'You'}, you are currently OFFLINE and NOT tracking time! Click "Punch In" immediately to resume work tracking!`,
        message_type: 'error',
        priority: 100
      });
    }

    setSystemMessages(statusMsgs);
  }, [isOnline, isIdle, userName]);

  // Combine all messages
  const allMessages = [...systemMessages, ...messages].sort((a, b) => (b.priority || 0) - (a.priority || 0));

  if (allMessages.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.05),
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 0.5,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          animation: `${scroll} ${Math.max(15, allMessages.length * 10)}s linear infinite`,
          whiteSpace: 'nowrap',
          '&:hover': {
            animationPlayState: 'paused'
          }
        }}
      >
        {allMessages.map((msg, index) => (
          <Box
            key={msg.id || index}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              mx: 4,
              animation: msg.message_type === 'warning' || msg.message_type === 'error' 
                ? `${flash} 2s ease-in-out infinite` 
                : 'none'
            }}
          >
            <Box sx={{ color: getMessageColor(msg.message_type), display: 'flex' }}>
              {getMessageIcon(msg.message_type)}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: getMessageColor(msg.message_type),
                fontWeight: 500,
                fontSize: '0.8rem'
              }}
            >
              {msg.message}
            </Typography>
            {index < allMessages.length - 1 && (
              <Box sx={{ mx: 3, display: 'flex', alignItems: 'center' }}>
                <StatusIcon sx={{ fontSize: 6, color: 'text.disabled' }} />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MarqueeBar;
